"use client"
import { FaCopy } from "react-icons/fa"

const OrderFormStep2 = ({
  handleStep2Submit,
  selectedSiteBalance,
  product,
  paymentAmount,
  handlePaymentAmountChange,
  accountDetails,
  copyAllToClipboard,
  copySuccess,
  setShowAccountWarning,
  handlePoFileChange,
  poUploaded,
  paymentMethod,
  handlePaymentMethodChange,
  isPaymentProofRequired,
  handlePaymentFileChange,
  paymentUploaded,
  utrNumber,
  handleUtrNumberChange,
  handleBackToStep1,
  isStep2Complete,
}) => {
  return (
    <form onSubmit={handleStep2Submit}>
      {/* Show site-specific balance if available */}
      {selectedSiteBalance > 0 && (
        <div className="balance-info-card">
          <div className="balance-header">
            <h3>Available Balance for {product.name}</h3>
          </div>
          <div className="balance-details">
            <div className="balance-item">
              <span className="balance-label">Site Balance:</span>
              <span className="balance-value">₹{selectedSiteBalance.toLocaleString("en-IN")}</span>
            </div>
          </div>
          <div className="balance-info-message">
            You can use your existing balance for this order or add more to your balance.
          </div>
        </div>
      )}
      <div className="orderForm-group">
        <label className="form-label-static payment-label mt-3">Payment Amount:</label>
        <div className="payment-amount-container">
          {selectedSiteBalance > 0 && (
            <div className="payment-amount-prefix">
              <span className="balance-plus">+</span>
            </div>
          )}
          <input
            type="text"
            id="paymentAmount"
            value={paymentAmount}
            onChange={handlePaymentAmountChange}
            required
            placeholder={selectedSiteBalance > 0 ? "0.00" : ""}
            maxLength={10}
            className="product-form-input"
          />
        </div>
        {(paymentAmount < 5000 || paymentAmount > 50000000) && paymentAmount !== "" && paymentAmount !== "0.00" && (
          <p className="error-message">Amount must be between ₹5000 and ₹5,00,00,000</p>
        )}
        {selectedSiteBalance > 0 && paymentAmount === "0.00" && (
          <div className="using-balance-info">
            <p>You are using your existing balance for this order.</p>
          </div>
        )}
      </div>

      <label htmlFor="depot-select fw-bold">Account Details:</label>
      {accountDetails ? (
        <div className="account-details-info">
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <button
              className="copy-button common-copy-button"
              onClick={() => copyAllToClipboard(accountDetails)}
              title="Copy all to clipboard"
              type="button"
            >
              <FaCopy />
              {copySuccess && <span className="copy-tooltip">Copied!</span>}
            </button>
          </div>
          {Object.entries(accountDetails).map(([key, value]) => (
            <div key={key} className="account-detail-item">
              <div className="detail-label">{key}:</div>
              <div className="detail-value">{value}</div>
            </div>
          ))}
        </div>
      ) : (
        <div className="no-account-details">
          No account details available for this site address
          <button type="button" className="verify-account-btn" onClick={() => setShowAccountWarning(true)}>
            Why is this happening?
          </button>
        </div>
      )}

      <div className="orderForm-row">
        <div className="orderForm-group">
          <div className="file-input-container">
            <label className="form-label-static payment-label mt-3">Upload PO:</label>
            <input
              type="file"
              className="file-input"
              id="poFile"
              accept=".png, .jpg, .jpeg"
              onChange={handlePoFileChange}
              disabled={!accountDetails}
            />
            <label
              htmlFor="poFile"
              className={`file-label ${poUploaded ? "uploaded" : ""} ${!accountDetails ? "disabled" : ""}`}
            >
              {poUploaded ? "PO Uploaded ✓" : "PO (Optional)"}
            </label>
          </div>
        </div>

        <div className="orderForm-group">
          <div className="payment-method-selection">
            <label className="form-label-static mt-3">Select Payment Method:</label>
            <div className="payment-method-options">
              <button
                type="button"
                className={`payment-method-btn ${paymentMethod === "proof" ? "active" : ""}`}
                onClick={() => handlePaymentMethodChange("proof")}
                disabled={!isPaymentProofRequired || !accountDetails}
              >
                Payment Proof
              </button>
              <span className="or-separator">OR</span>
              <button
                type="button"
                className={`payment-method-btn ${paymentMethod === "utr" ? "active" : ""}`}
                onClick={() => handlePaymentMethodChange("utr")}
                disabled={!isPaymentProofRequired || !accountDetails}
              >
                UTR Number
              </button>
            </div>
          </div>

          {paymentMethod === "proof" && (
            <div className="file-input-container">
              <input
                type="file"
                className="file-input"
                id="paymentFile"
                accept=".png, .jpg, .jpeg"
                onChange={handlePaymentFileChange}
                disabled={!isPaymentProofRequired || !accountDetails}
              />
              <label
                htmlFor="paymentFile"
                className={`file-label ${paymentUploaded ? "uploaded" : ""} ${!isPaymentProofRequired || !accountDetails ? "disabled" : ""}`}
              >
                {!isPaymentProofRequired
                  ? "Payment Covered by Balance"
                  : paymentUploaded
                    ? "Payment Uploaded ✓"
                    : "Upload Payment Proof"}
              </label>
            </div>
          )}

          {paymentMethod === "utr" && (
            <div className="utr-input-container">
              <input
                type="text"
                id="utrNumber"
                value={utrNumber}
                onChange={handleUtrNumberChange}
                placeholder="Enter 12 to 22 alphanumeric characters of UTR number"
                className="product-form-input"
                maxLength={22}
                disabled={!isPaymentProofRequired || !accountDetails}
              />

              {utrNumber && utrNumber.length >= 12 && !/^[A-Za-z0-9]{12,22}$/.test(utrNumber) && (
                <p className="error-message">
                  UTR number must be 12 to 22 alphanumeric characters (no spaces or symbols).
                </p>
              )}
            </div>
          )}

          {!isPaymentProofRequired && (
            <p className="balance-note">Your payment will be covered by your available balance.</p>
          )}
        </div>
      </div>

      <div className="text-center" style={{ marginTop: "-20px" }}>
        <strong className="text-danger">Note:</strong>{" "}
        <span className="text-danger">
          Every file should not exceed <strong>5MB</strong> in size.
        </span>
      </div>

      <div className="action-buttons">
        <button type="button" className="action-btn back-btn" onClick={handleBackToStep1}>
          Back
        </button>
        <button
          type="submit"
          className={`action-btn order-now ${isStep2Complete() ? "active" : ""}`}
          disabled={!isStep2Complete()}
        >
          Order Now
        </button>
      </div>
    </form>
  )
}

export default OrderFormStep2
